<template>
  <!-- A file of a message: in the composer while it uploads (progress, error, removal), in the thread
       as a button that opens its card. -->
  <component
    :is="openable ? 'button' : 'div'"
    :type="openable ? 'button' : null"
    class="ai-attachment"
    :class="{ 'is-error': !!error, 'is-openable': openable }"
    :title="title"
    @click="openable && $emit('open')"
  >
    <ai-icon
      :name="error ? 'alert' : 'file'"
      :size="15"
      class="ai-attachment__icon"
    />
    <span class="ai-attachment__body">
      <span class="ai-attachment__name">{{ name }}</span>
      <span class="ai-attachment__meta">{{ meta }}</span>
    </span>
    <button
      v-if="removable"
      type="button"
      class="ai-attachment__remove"
      :title="uploading ? 'Отменить загрузку' : 'Убрать файл'"
      :aria-label="uploading ? `Отменить загрузку ${name}` : `Убрать файл ${name}`"
      @click.stop="$emit('remove')"
    >
      <ai-icon
        name="close"
        :size="12"
      />
    </button>
    <span
      v-if="uploading"
      class="ai-attachment__progress"
      :style="{ width: `${percent}%` }"
    />
  </component>
</template>

<script>
  import AiIcon from './AiIcon.vue';
  import { fileSize } from './format';

  export default {
    name: 'AttachmentChip',
    components: { AiIcon },
    props: {
      name: { type: String, default: '' },
      // Bytes (or a ready label).
      size: { type: [Number, String], default: null },
      // Type label of the service («Документ Word»…), shown in the hint.
      type: { type: String, default: '' },
      // 0..1 while the file uploads, null otherwise.
      progress: { type: Number, default: null },
      error: { type: String, default: '' },
      removable: { type: Boolean, default: false },
      openable: { type: Boolean, default: false },
    },
    computed: {
      uploading() {
        return this.progress !== null && !this.error;
      },
      percent() {
        return Math.round(Math.min(Math.max(this.progress || 0, 0), 1) * 100);
      },
      meta() {
        if (this.error) return this.error;
        // The transfer is over; the file is being read before the answer.
        if (this.uploading) return this.percent >= 100 ? 'Обработка…' : `${this.percent}%`;
        return fileSize(this.size);
      },
      title() {
        const parts = [this.name, this.type, fileSize(this.size)].filter(Boolean).join(' · ');
        if (this.error) return `${parts}\n${this.error}`;
        return this.openable ? `${parts}\nПоказать карточку файла` : parts;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .ai-attachment {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    box-sizing: border-box;
    max-width: 260px;
    min-width: 0;
    padding: 5px 6px 5px 8px;
    border: 1px solid var(--ui-border) !important;
    border-radius: var(--ui-radius-sm) !important;
    background: var(--ui-surface-soft);
    color: var(--ui-text);
    font: inherit;
    font-size: 12px;
    line-height: 1.3;
    text-align: left;
    overflow: hidden;

    &.is-openable {
      padding-right: 10px;
      background: var(--ui-surface);
      cursor: pointer;

      &:hover {
        border-color: var(--ui-primary) !important;

        .ai-attachment__name {
          color: var(--ui-primary);
        }
      }
    }

    &.is-error {
      border-color: var(--ui-danger) !important;

      .ai-attachment__icon,
      .ai-attachment__meta {
        color: var(--ui-danger);
      }

      // The reason is read in full.
      .ai-attachment__meta {
        white-space: normal;
      }
    }
  }

  .ai-attachment__icon {
    color: var(--ui-text-muted);
  }

  .ai-attachment__body {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-width: 0;
  }

  .ai-attachment__name,
  .ai-attachment__meta {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ai-attachment__name {
    font-weight: 500;
  }

  .ai-attachment__meta {
    color: var(--ui-text-muted);
    font-size: 11px;
  }

  .ai-attachment__remove {
    display: inline-flex;
    flex: 0 0 20px;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    border-radius: 50% !important;
    background: transparent;
    color: var(--ui-text-muted);
    cursor: pointer;

    &:hover {
      background: var(--ui-bg-strong);
      color: var(--ui-text);
    }
  }

  .ai-attachment__progress {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 2px;
    background: var(--ui-primary);
    transition: width 0.2s ease;
  }
</style>
